'use client';
import type { ExperimentClient } from '@amplitude/experiment-js-client';
import { logger } from '@magicschool/logger';
import { type ThemeName, useTheme } from '@magicschool/spellbook';
import type { MagicSchoolUser } from '@magicschool/supabase/user';
import { useEffect, useRef } from 'react';
import type { TeacherProfileWithRelations } from '@/app/(teacher)/profile/_actions/types';
import { useCookieYesConsent } from '@/components/CookieYes/useCookieYesConsent';
import { initAmplitudeAnalytics, initAmplitudeExperiment, revokeAnalyticsConsent } from '@/util/amplitude/analyticsClient';

export type AmplitudeInitializerProps = {
  user?: MagicSchoolUser;
  teacherProfile?: TeacherProfileWithRelations;
  initialVariants?: ExperimentClient['variants'];
};

export const AmplitudeInitializer = ({ user, teacherProfile, initialVariants }: AmplitudeInitializerProps) => {
  const hasInitializedExperimentRef = useRef(false);
  const hasInitializedAnalyticsRef = useRef(false);
  const { theme, systemTheme } = useTheme();
  const cookieConsent = useCookieYesConsent();

  // Feature flags must work regardless of consent, so this runs unconditionally. Also recovers
  // client-side (independent of consent) if the server prefetch failed or was skipped.
  useEffect(() => {
    if (hasInitializedExperimentRef.current) return;

    const init = async () => {
      try {
        await initAmplitudeExperiment({
          user,
          teacherProfile: teacherProfile || undefined,
          appTheme: theme as ThemeName,
          userSystemTheme: systemTheme,
          initialVariants,
        });
        hasInitializedExperimentRef.current = true;
      } catch (error) {
        logger.error('Failed to initialize Amplitude experiment client:', error);
      }
    };
    init();
  }, [initialVariants, user, teacherProfile, theme, systemTheme]);

  useEffect(() => {
    if (!cookieConsent.analytics) {
      if (!hasInitializedAnalyticsRef.current) return;
      hasInitializedAnalyticsRef.current = false;
      revokeAnalyticsConsent();
      return;
    }

    if (hasInitializedAnalyticsRef.current) return;

    const init = async () => {
      try {
        await initAmplitudeAnalytics({
          user,
          teacherProfile: teacherProfile || undefined,
          appTheme: theme as ThemeName,
          userSystemTheme: systemTheme,
        });
        hasInitializedAnalyticsRef.current = true;
      } catch (error) {
        logger.error('Failed to initialize/update analytics:', error);
      }
    };
    init();
  }, [cookieConsent.analytics, user, teacherProfile, theme, systemTheme]);

  return null;
};

export default AmplitudeInitializer;
